import React, { useMemo, useState } from "react";
import ModalShell from "./modals/ModalShell";
import NumpadPopover from "./NumpadPopover";
import RosterCombobox from "./RosterCombobox";
import { mockCourses } from "./mockCourses";
import { loadCourses } from "./coursesDb";
import { PUBLIC_PLAYER } from "./roster";

export interface EditablePerson {
  name: string;
  handicapIndex?: number;
  // Owner/profile-only. Surfaced via the showHomeCourse prop so it never
  // appears on regular roster-player edits.
  homeCourse?: string;
  // Owner-only preference. Surfaced via the showCurrency prop below so
  // the field is hidden on regular roster edits. Stored as the literal
  // symbol ("$" / "€") for cheap display elsewhere.
  betCurrency?: string;
}

interface Props {
  initial?: EditablePerson;
  title?: string;
  // When true, render the "Bet Currency" dropdown (used on the Set up
  // owner / Edit owner forms). Roster-player edits leave it off.
  showCurrency?: boolean;
  // When true, render the editable "Home Course" search field. Profile
  // (owner) only — deliberately NOT shown on roster-player edits.
  showHomeCourse?: boolean;
  // A connected player's home course, shown read-only in their details to
  // disambiguate same-name users. Kept out of the roster list on purpose.
  connectedHomeCourse?: string;
  // Profile (owner) only — when provided, renders the destructive
  // "Delete account" action at the bottom of the form.
  onDeleteAccount?: () => void;
  onSave: (data: EditablePerson) => void;
  onClose: () => void;
}

const CURRENCY_CHOICES: Array<{ value: string; label: string }> = [
  { value: "$", label: "$" },
  { value: "€", label: "€" },
  { value: "£", label: "£" },
  { value: "¥", label: "¥" },
];

// Add / edit a person (owner or roster). Same compact-input vocabulary as
// the in-round PlayersModal so the look is consistent across the module:
// small uppercase column-style labels above each input, and the same
// calculator-style NumpadPopover for the handicap index.
export default function PlayerEditModal({
  initial,
  title,
  showCurrency = false,
  showHomeCourse = false,
  connectedHomeCourse,
  onDeleteAccount,
  onSave,
  onClose,
}: Props) {
  const [name, setName] = useState(initial?.name || "");
  const [handicapIndex, setHandicapIndex] = useState<number | undefined>(
    initial?.handicapIndex,
  );
  // Home Course pre-fills with "Public Player" on the profile so it's never
  // blank (it drives Connect search). Only defaulted when the field is shown
  // (owner profile); regular roster edits don't carry a home course.
  const [homeCourse, setHomeCourse] = useState<string>(
    initial?.homeCourse || (showHomeCourse ? PUBLIC_PLAYER : ""),
  );
  const [betCurrency, setBetCurrency] = useState<string>(
    initial?.betCurrency || "$",
  );

  // Searchable course suggestions = saved courses + static fixtures,
  // deduped by name. Free text is still allowed (a home club the user
  // hasn't added as a course yet).
  const courseOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ id: string; name: string }> = [];
    for (const c of [...loadCourses(), ...mockCourses]) {
      const key = c.name.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push({ id: c.id, name: c.name });
    }
    return out;
  }, []);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave({
      name: trimmed,
      handicapIndex,
      // Never persist a blank home course — fall back to "Public Player" so
      // every profile stays searchable in Connect.
      ...(showHomeCourse
        ? { homeCourse: homeCourse.trim() || PUBLIC_PLAYER }
        : {}),
      ...(showCurrency ? { betCurrency } : {}),
    });
  };

  return (
    <ModalShell
      title={title || (initial ? "Edit player" : "Add player")}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="cg-btn cg-btn-ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cg-btn cg-btn-primary"
            onClick={submit}
            disabled={!name.trim()}
          >
            Save
          </button>
        </>
      }
    >
      <div className="cg-edit-form">
        <div className="cg-edit-field">
          <label className="cg-edit-label" htmlFor="cg-edit-name">
            Player
          </label>
          <input
            id="cg-edit-name"
            className="cg-player-name cg-edit-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            autoFocus
          />
        </div>

        <div className="cg-edit-field">
          <label className="cg-edit-label">Handicap Index</label>
          <NumpadPopover
            value={handicapIndex}
            onChange={setHandicapIndex}
            min={0}
            max={54}
            maxDecimals={1}
            displayDecimals={1}
            ariaLabel="Handicap index"
            className="cg-edit-hcp-num"
          />
        </div>

        {connectedHomeCourse && (
          <div className="cg-edit-field">
            <label className="cg-edit-label">Home Course</label>
            <div className="cg-edit-static">{connectedHomeCourse}</div>
          </div>
        )}

        {showHomeCourse && (
          <div className="cg-edit-field">
            <label className="cg-edit-label">Home Course</label>
            <RosterCombobox
              value={homeCourse}
              onChange={setHomeCourse}
              options={courseOptions}
              placeholder="Search courses"
              className="cg-edit-course-combo"
            />
            <p className="cg-edit-help">
              Add your home course to make it easier for friends and fellow
              members to connect and share round information. If you are not
              attached to a course, leave this as Public Player.
            </p>
          </div>
        )}

        {showCurrency && (
          <div className="cg-edit-field">
            <label className="cg-edit-label" htmlFor="cg-edit-currency">
              Bet Currency
            </label>
            <select
              id="cg-edit-currency"
              className="cg-player-tee cg-edit-input"
              value={betCurrency}
              onChange={(e) => setBetCurrency(e.target.value)}
            >
              {CURRENCY_CHOICES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {onDeleteAccount && (
          <div className="cg-edit-danger">
            <button
              type="button"
              className="cg-edit-delete-account"
              onClick={onDeleteAccount}
            >
              Delete account
            </button>
          </div>
        )}
      </div>
    </ModalShell>
  );
}
